import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { navigate } from '@/lib/router/useHashRoute';
import { signOut, useAuth } from '@/lib/auth/useAuth';
import { ContactList } from '@/components/ContactList';
import { getSupabase } from '@/lib/supabase';
import { useViewport } from '@/lib/viewport/useViewport';
import type { UserProfile } from '@/lib/auth/useUserProfile';
import { useRoleTranslationRefresh } from '@/lib/i18n/useRoleTranslationRefresh';
import { isLang, type Lang } from '@/types/lang';

/**
 * Editorial countdown helper — days until wedding (5 Sep 2026).
 * Same date is hardcoded in greetings-draft.md / special_dates seed.
 * If today is past the wedding, returns 0 and the banner falls back to a
 * post-wedding evergreen label (handled inline below).
 */
const WEDDING_DATE = new Date('2026-09-05T00:00:00');
function daysUntilWedding(): number {
  const now = new Date();
  const diffMs = WEDDING_DATE.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diffMs / (24 * 60 * 60 * 1000)));
}

function firstInitial(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '·';
  return trimmed.charAt(0).toUpperCase();
}

/**
 * Home — Family (CLAUDE.md §6.3 contact list)
 *
 * Phase 1: contact list of family members + family-code share button.
 * The ContactList component handles RLS-filtered users + 1-on-1 conversation
 * navigation via the conversation-find-or-create Edge Function.
 *
 * Phase 1.5 / stretch (CLAUDE.md §6 Stretch S1): a "Yüzyüze konuş" section
 * lists each contact again as a one-tap entry into face-to-face mode.
 * Re-uses the same TanStack Query cache key as ContactList so the network
 * fetch only happens once.
 *
 * Tablet (A6): on iPad two-pane, the sidebar already shows ContactList and
 * face-to-face entries, so this screen renders only the welcome card +
 * family-code CTA. The sign-out lives in the sidebar's footer.
 */

interface HomeProps {
  profile: UserProfile;
}

interface ContactRow {
  id: string;
  name: string;
  role_label: string;
  role_label_translations: Record<string, string> | null;
}

export function Home({ profile }: HomeProps) {
  const { t, i18n } = useTranslation();
  const uiLang: Lang = isLang(i18n.language) ? i18n.language : 'tr';
  const { session } = useAuth();
  const myId = session?.user.id ?? null;
  const viewport = useViewport();
  const isTablet = viewport === 'tablet';

  // Same query key as ContactList → cache hit, no duplicate network fetch.
  // Used only on phone — tablet shows F2F entries inside the sidebar.
  const contactsQuery = useQuery({
    queryKey: ['contacts', myId],
    enabled: Boolean(myId) && !isTablet,
    queryFn: async (): Promise<ContactRow[]> => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from('users')
        .select('id, name, role_label, role_label_translations')
        .order('name', { ascending: true });
      if (error) throw error;
      const rows = (data ?? []).map<ContactRow>((u) => ({
        id: u.id as string,
        name: u.name as string,
        role_label: u.role_label as string,
        role_label_translations:
          u.role_label_translations && typeof u.role_label_translations === 'object'
            ? (u.role_label_translations as Record<string, string>)
            : null,
      }));
      return rows.filter((u) => u.id !== myId);
    },
    refetchInterval: 8000,
  });

  const contacts = contactsQuery.data ?? [];
  const days = daysUntilWedding();
  const monogram = firstInitial(profile.name);

  // Oturum 9 00:10 — Role label translation auto-refresh: family member'lar
  // listesinde role_label_translations[uiLang] eksikse Edge Function tetikle.
  // Hook session-local dedup yapar, infinite loop yok.
  useRoleTranslationRefresh(contacts, uiLang);

  return (
    <main className="min-h-screen bg-bg-app text-text-primary font-sans px-4 py-6 max-w-2xl mx-auto">
      {!isTablet ? (
        <header className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif italic text-2xl text-text-primary leading-none">
              Family
            </h1>
            <div className="w-9 h-px bg-accent-gold mt-1" aria-hidden="true" />
          </div>
          <button
            type="button"
            onClick={() => navigate('/settings')}
            aria-label={t('home.settingsButton')}
            className="min-h-tap min-w-tap rounded-xl border border-accent-gold text-accent-gold-dark bg-bg-elevated flex items-center justify-center text-base hover:bg-bg-bubble-received/40"
          >
            ⚙
          </button>
        </header>
      ) : null}

      {/* Wedding countdown banner — editorial centerpiece on the home screen.
          Hides past-wedding to avoid a stale "0 gün" reading. */}
      {days > 0 ? (
        <section className="invite-frame mb-6 p-5 bg-bg-elevated text-center shadow-warm-sm">
          <p className="eyebrow">{t('home.title')}</p>
          <p className="font-serif italic text-3xl text-accent-primary mt-1">
            {t('home.weddingCountdown', { count: days })}
          </p>
          <div className="ornament-divider my-2" aria-hidden="true">
            <span>❀</span>
          </div>
          <p className="font-serif italic text-sm text-text-secondary">
            {(() => {
              // Bug F fix (Oturum 8): tarihi her dilde yerel formatla render et.
              // tr → "5 Eylül 2026", en → "September 5, 2026", bs → "5. septembar 2026.",
              // de → "5. September 2026". Names proper noun, çevrilmez.
              const localeMap: Record<string, string> = {
                tr: 'tr-TR',
                en: 'en-US',
                bs: 'bs-BA',
                de: 'de-DE',
              };
              const bcp47 = localeMap[i18n.language] ?? 'en-US';
              const dateStr = new Intl.DateTimeFormat(bcp47, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              }).format(WEDDING_DATE);
              return t('home.weddingTagline', { date: dateStr });
            })()}
          </p>
        </section>
      ) : null}

      {/* Welcome card — monogram avatar + eyebrow context label + Playfair name. */}
      <section className="mb-6 p-5 rounded-2xl bg-bg-elevated border border-border-subtle shadow-warm-sm">
        <div className="flex items-start gap-4">
          <span aria-hidden="true" className="monogram-avatar w-12 h-12 text-xl">
            {monogram}
          </span>
          <div className="flex-1 min-w-0">
            <p className="eyebrow">{t('home.welcome', { name: '' })}</p>
            <h2 className="font-serif italic text-xl text-text-primary leading-tight mt-0.5 truncate">
              {profile.name}
            </h2>
            <p className="text-sm text-text-secondary mt-1 font-serif italic">
              {t('home.shareFamilyCodeNote')}
            </p>
            <button
              type="button"
              onClick={() => navigate('/family-code')}
              className="mt-3 inline-flex items-center min-h-tap px-4 py-2 rounded-xl border border-accent-gold text-accent-gold-dark text-sm hover:bg-bg-bubble-received/40 transition-colors duration-150"
            >
              ✦ &nbsp;{t('home.showFamilyCodeButton')}
            </button>
          </div>
        </div>
      </section>

      {/*
        Yüzyüze konuş — F2F erişim butonu (Oturum 9 01:10, Begüm UX kararı).
        F2F sadece aile üyesi tıklayarak değil, bağımsız bir özellik olarak
        açılabilmeli — "websitesinin bir ozelligi gibi olmali" (Begüm).
        /face-to-face (no id) route'una gider; F2F bileşeni contactId'siz
        açılır, her iki taraf chip'lerden dilini seçer ve konuşma başlar.
      */}
      <button
        type="button"
        onClick={() => navigate('/face-to-face')}
        aria-label={t('home.startF2F')}
        className="w-full min-h-tap px-6 py-5 rounded-2xl bg-accent-primary text-white font-serif italic text-xl shadow-warm-md hover:opacity-90 active:scale-[0.99] transition-all duration-150 mb-8 flex items-center justify-center gap-3"
      >
        <span aria-hidden="true">🎙</span>
        <span>{t('home.startF2F')}</span>
      </button>

      {!isTablet ? (
        <>
          <section className="mb-8">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="font-serif italic text-2xl text-text-primary">
                {t('home.contactsHeading')}
              </h2>
              {contacts.length > 0 ? (
                <span className="eyebrow">
                  {t('home.contactCount', { count: contacts.length })}
                </span>
              ) : null}
            </div>
            <ContactList />
          </section>

          {/*
            Oturum 9 04:00 — F2F per-contact listesi KALDIRILDI.
            Begüm UX kararı (Oturum 9 01:10): "F2F kişiler arası bir özellik
            gibi değil, websitesinin özelliği gibi olmalı". Üstteki büyük
            "🎙 Yüzyüze konuş" buton bu işi yapıyor → her contact için ayrı
            F2F entry duplication yaratıyordu. Aile listesi (chat için) kalır,
            F2F için tek erişim noktası = üstteki big button.
          */}

          <footer className="text-center">
            <div className="ornament-divider mb-3" aria-hidden="true">
              <span>❀</span>
            </div>
            <button
              type="button"
              onClick={() => void signOut()}
              className="min-h-tap px-4 py-2 text-sm text-accent-link gold-underline"
            >
              {t('settings.signOut')}
            </button>
          </footer>
        </>
      ) : null}
    </main>
  );
}
